import { useEffect, useRef } from 'react';
import { useStyles, Button, pxToRem, Flex } from '@archipro-design/aria';
import type { ButtonProps } from '@archipro-design/aria';
import { AnimatePresence, motion } from 'framer-motion';
import { InPageSearchBar } from '@modules/search/components/in-page-search-bar/InPageSearchBar';
import * as S from './SearchFilter.style';
import { useProfessionalTabSearch } from '~/modules/professional/hook/use-professional-tab-search';
import { useCurrentTabPath } from '~/modules/professional/hook/use-current-tab-path';
import FilterDrawer from '@modules/search/components/filter-drawer/FilterDrawer';
import { useSearchFilters } from '@modules/search/hook/use-search-filters';
import { useToggle } from 'ahooks';
import type {
    FilterBarCheckboxFilter,
    FilterStates,
} from '~/modules/search/type/filter-state';
import { useMatchesHandleData } from '~/modules/root/hook';
import type { SearchIndex } from '~/modules/search/type';
import { IN_PAGE_SEARCH_IGNORE_FILTER_KEYS } from '~/modules/professional/config/constants';
import { useSearchSorts } from '~/modules/search/hook/use-search-sorts';
import {
    CheckboxBlankLine,
    CheckboxFill,
    Filter,
    SearchLine,
} from '@archipro-design/icons';
import { SHOP_FILTER } from '~/modules/product/config/product-filter-config';

interface SearchFilterProp {
    initialStates?: FilterStates | null;
}

export const SearchFilterMobile: React.FC<SearchFilterProp> = ({
    initialStates = null,
}) => {
    const currentTabPath = useCurrentTabPath();
    const { css } = useStyles({ currentTabPath });

    const selectedFilterButtonRef = useRef<HTMLDivElement>(null);

    const tabSearch = useProfessionalTabSearch();

    const category = useMatchesHandleData<SearchIndex>('category', 'project');

    const ignoreFilterKeys = IN_PAGE_SEARCH_IGNORE_FILTER_KEYS;

    const [filterDrawerOpen, { set: setFilterDrawerState }] = useToggle(false);

    const [
        searchDrawerOpen,
        { toggle: toggleSearchDrawer, set: setSearchDrawer },
    ] = useToggle(false);

    const {
        filters,
        filterStates,
        filterBarFilters,
        resetFilters,
        updateFilter,
    } = useSearchFilters({
        category,
        initialStates,
        isCategoryPage: false,
        ignoreFilterKeys,
    });

    const shopFilter: FilterBarCheckboxFilter | undefined =
        filterBarFilters?.find((f) => f.filterKey === SHOP_FILTER.filterKey);

    const { sortOptions } = useSearchSorts({
        category,
    });

    useEffect(() => {
        if (!selectedFilterButtonRef.current) return;
        selectedFilterButtonRef.current.scrollIntoView({
            inline: 'center',
            block: 'center',
        });
    }, [sortOptions]);

    // Open search drawer if there is a default keyword
    useEffect(() => {
        if (tabSearch.defaultKeyword) {
            setSearchDrawer(true);
        }
    }, [tabSearch.defaultKeyword, setSearchDrawer]);

    const buttonProps = {
        size: 14,
        transparent: true,
        variables: () => ({
            buttonPadding: 10,
        }),
        className: css(S.MobileButtonFilters),
    } as ButtonProps;

    return (
        <>
            <div className={css(S.MobileFilterWarp)}>
                <Flex vAlign="center">
                    <Button
                        iconBefore={<Filter aria-label={'filter'} />}
                        onClick={() => setFilterDrawerState(true)}
                        {...buttonProps}
                    >
                        Filters
                    </Button>
                    {shopFilter && (
                        <Button
                            key={SHOP_FILTER.filterKey}
                            variant="text"
                            size={18}
                            variables={{
                                buttonPadding: pxToRem(10),
                                iconPadding: 0,
                                buttonSize18TextFontSize: pxToRem(14),
                                buttonSize18LineHeight: pxToRem(14),
                            }}
                            iconBefore={
                                shopFilter.checked ? (
                                    <CheckboxFill />
                                ) : (
                                    <CheckboxBlankLine />
                                )
                            }
                            disabled={shopFilter.disabled}
                            onClick={() => {
                                updateFilter(
                                    SHOP_FILTER.filterKey,
                                    shopFilter.newState
                                );
                            }}
                            className={css(S.MobileButtonFilters)}
                        >
                            {shopFilter.label}
                        </Button>
                    )}
                </Flex>
                <Button
                    iconAfter={<SearchLine aria-label={'search'} />}
                    onClick={() => toggleSearchDrawer()}
                    {...buttonProps}
                >
                    Search
                </Button>
            </div>
            <AnimatePresence>
                {searchDrawerOpen && (
                    <motion.div
                        initial={{ height: 0 }}
                        animate={{ height: pxToRem(57) }}
                        exit={{ height: 0 }}
                        className={css(S.MobileSearchDrawer)}
                    >
                        <InPageSearchBar
                            {...tabSearch}
                            toggleSearchDrawer={() => toggleSearchDrawer()}
                        />
                    </motion.div>
                )}
            </AnimatePresence>

            <FilterDrawer
                open={filterDrawerOpen}
                onOpenChange={(_, data) => setFilterDrawerState(!!data)}
                filterSections={filters ?? []}
                filterStates={filterStates}
                category={category}
                onApplyFilters={(_, data) => {
                    setFilterDrawerState(false);
                    resetFilters(data ?? {});
                }}
                ignoreFilterKeys={ignoreFilterKeys}
            />
        </>
    );
};
